import { SharedConfigStoreState } from '@editor/stores/sharedConfigStore/types';
import { User } from '@repo/domain/user';

type RequireAtLeastOneOf<T, Keys extends keyof T> = Omit<T, Keys> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Omit<T, K>>;
  }[Keys];

export type GetItemsFn = (
  user: User | null,
  sharedConfig: SharedConfigStoreState['config']
) => ReadonlyArray<React.ReactNode>;

interface UpgradeModalProps {
  isOpen: boolean;
  successModalIsOpen: boolean;
  contentType: UpgradeModalContentType;
  designPreview: string;
  imageComponentType: ImageComponentType;
  onClose: () => void;
  onSuccess: () => void;
  returnUrlParams: Record<string, string>;
}

export interface UpgradeModalContentProps
  extends Omit<UpgradeModalProps, 'isOpen' | 'onClose' | 'successModalIsOpen'> {
  skipIncentive?: boolean;
  onSuccess: () => void;
  onClose: () => void;
  returnUrlFailed?: boolean;
  isOnlyExpertUpgrade?: boolean;
  resetReturnUrlFailed?: () => void;
  onClickCallback?: () => void;
}

export type UpgradeModalConfigData = RequireAtLeastOneOf<
  {
    title: string;
    tag?: string;
    description?: string;
    showOnlyOnePlan?: boolean; // only give option of EXPERT plan when already on PRO
    items: readonly React.ReactNode[];
    itemsTitle?: string;
    content: React.ReactNode;
    buttonLabel?: string;
    upgradeButtonDescription?: string;
    hideButtonDescription?: boolean;
    secondaryButtonLabel?: string;
    onlyExpert?: boolean;
    headerText?: string;
  },
  'items' | 'content'
>;

export interface UpgradeModalIncentiveContentBuilderProps
  extends Omit<Partial<UpgradeModalConfigData>, 'getDescription'> {
  hideImageOnMobile: boolean;
  background: string;
  imageComponent: React.ReactNode;
  imageComponentType?: ImageComponentType;
  onUpgrade: UpgradeModalIncentiveContentProps['onUpgrade'];
  buttonLabel?: string;
  upgradeButtonDescription?: string;
  hideButtonDescription?: boolean;
  secondaryButtonLabel?: string;
  onSecondaryButtonClick?: () => void;
  dataTestId?: string;
  headerText?: string;
}

export type ImageComponentType =
  | 'quote'
  | 'quoteV2'
  | 'testimonials'
  | 'brands'
  | 'designPreview'
  | 'aiImages'
  | 'backgroundReplacer'
  | 'upgradeToPaidPlan'
  | 'logoGenUpgrade'
  | 'designGenUpgrade'
  | 'copilotTemplates'
  | 'premiumElements'
  | 'aiSaveStyleUpgradeToExpert'
  | 'eraseFill'
  | 'none';

export const upgradeModalContent = [
  'highResVectorDownload',
  'premiumDesignUsage',
  'premiumBackgroundUsage',
  'premiumTextureUsage',
  'premiumElementUsage',
  'premiumCopilotElementsUsage',
  'premiumCopilotTemplatesUsage',
  'premiumLayoutUsage',
  'premiumMockupUsage',
  'projectLimitReached',
  'proProjectLimitReached',
  'freeUploadLimitReached',
  'proUploadLimitReached',
  'removeBackground',
  'uploadCustomFonts',
  'customArtboardSize',
  'improveImageQuality',
  'removeKittlLogo',
  'logoGenUpgrade',
  'premiumColorPalettes',
  'imageBackgroundRemoval',
  'unlimitedBackgroundRemoval',
  'unlimitedImageUpscaling',
  'upgradePlanAsFreeUserAiPanel',
  'upgradePlanWithMoreCreditsAsFreeUserAiPanel',
  'upgradePlanWithMoreCreditsAsProUserAiPanel',
  'buyMoreCreditsAsExpertUserAiPanel',
  'aiRateLimit',
  'upgradePlanWithMoreCreditsAIPanel',
  'imageGenV2Upgrade',
  'vectorizing',
  'podPresets',
  'podDiscounts',
  'backgroundReplacer',
  'upgradeToPaidPlanKittlPro',
  'standAlonePagesTools',
  'upgradeToPaidPlan',
  'upgradeToPaidPlanKittlPro',
  'freeTrial',
  'aiImageUpscaler',
  'aiImageUpscalerCreditsPro',
  'aiSaveAsStyle',
  'aiSaveStyleUpgradeToExpert',
  'librariesUpgradeToPaidPlan',
  'unlimitedLibrariesContent',
  'designGenUpgrade',
  'privateModeUpgrade',
  'eraseFill',
  'noIncentiveUpgrade',
] as const;

export type UpgradeModalContentType = (typeof upgradeModalContent)[number];

export interface UpgradeModalIncentiveContentProps {
  /**
   * Possible types of content
   */
  contentType: UpgradeModalContentType;
  /**
   * Component to display above image
   */
  imageComponentType: ImageComponentType;
  /**
   * the objectName for the design preview image
   */
  designPreview: string;
  /**
   * the User object
   */
  user: User | null;
  /**
   * onUpgrade action
   */
  onUpgrade: () => void;
  /**
   * onSecondaryButtonClick action
   */
  onSecondaryButtonClick?: () => void;
  /**
   * Value for the `data-testid` attribute used for E2E testing
   */
  dataTestId?: string;
}
